import React, { useEffect, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { ViewMode, ChartOption } from './types';

interface ChartModalProps {
  open: boolean;
  title: string;
  option: ChartOption;
  onClose: () => void; // ✅ NEW
  viewMode?: ViewMode;
  onToggleViewMode?: (m: ViewMode) => void;
}

const ChartModal: React.FC<ChartModalProps> = ({
  open,
  title,
  option,
  onClose, // ✅ NEW
  viewMode,
  onToggleViewMode,
}) => {
  const chartRef = useRef<ReactECharts | null>(null);

  useEffect(() => {
    if (!open) return;

    const t1 = window.setTimeout(
      () => chartRef.current?.getEchartsInstance()?.resize(),
      0
    );
    const t2 = window.setTimeout(
      () => chartRef.current?.getEchartsInstance()?.resize(),
      150
    );

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [open, option]);

  if (!open) return null;

  return (
    // ✅ Click on overlay closes
    <div style={styles.modalOverlayStyle} onClick={onClose}>
      {/* ✅ Stop bubbling so clicks inside don't close */}
      <div style={styles.modalContentStyle} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeaderStyle}>
          {/* Left: View mode toggles (or spacer to keep title centered) */}
          {viewMode && onToggleViewMode ? (
            <div style={styles.viewModeToggleContainerStyle}>
              <button
                onClick={() => onToggleViewMode('weekly')}
                style={
                  viewMode === 'weekly'
                    ? styles.viewModeButtonActiveOrangeStyle
                    : styles.viewModeButtonInactiveStyle
                }>
                Weekly
              </button>
              <button
                onClick={() => onToggleViewMode('monthly')}
                style={
                  viewMode === 'monthly'
                    ? styles.viewModeButtonActivePurpleStyle
                    : styles.viewModeButtonInactiveStyle
                }>
                Monthly
              </button>
            </div>
          ) : (
            <div style={{ width: 120 }} />
          )}

          {/* Center: Title */}
          <h2 style={styles.modalTitleStyle}>{title}</h2>

          {/* Right: Close (X) */}
          <button
            onClick={onClose} // ✅ FIX
            style={styles.modalCloseButtonStyle}
            aria-label="Close"
            title="Close">
            ✕
          </button>
        </div>

        <div style={styles.modalChartContainerStyle}>
          <ReactECharts
            ref={chartRef}
            option={option}
            style={{ height: '100%', width: '100%' }}
          />
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  modalOverlayStyle: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
  modalContentStyle: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow:
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    width: '92vw',
    height: '82vh',
    padding: '16px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  modalHeaderStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  modalTitleStyle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#4A4A4A',
    textAlign: 'center',
    flex: 1,
    margin: 0,
  },
  modalCloseButtonStyle: {
    width: 34,
    height: 34,
    borderRadius: 9999,
    border: '1px solid #e5e7eb',
    backgroundColor: '#ffffff',
    color: '#4A4A4A',
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
    fontSize: 16,
    fontWeight: 600,
  },
  modalChartContainerStyle: {
    flex: 1,
    minHeight: 0,
  },
  viewModeToggleContainerStyle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  viewModeButtonActiveOrangeStyle: {
    padding: '10px 16px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 500,
    backgroundColor: '#F15A29',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
  },
  viewModeButtonActivePurpleStyle: {
    padding: '10px 16px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 500,
    backgroundColor: '#662D91',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
  },
  viewModeButtonInactiveStyle: {
    padding: '10px 16px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 500,
    backgroundColor: '#e5e7eb',
    color: '#4A4A4A',
    border: 'none',
    cursor: 'pointer',
  },
};

export default ChartModal;
